import { IActionResponse } from '../../dal/BaseDAL';
import { ICollectionResponse } from '../../models/ICollectionResponse';
import {ICountResponse, ISavedSearchRef} from '../../models/Search';
import { ISavedSearch } from '../../models/Search';
import { cloneDeep } from 'lodash';
import api, { SEARCH_RESULTS_TYPE } from '../Api';

const savedSearchApi = api.injectEndpoints({
    endpoints: (build) => ({
        getSavedSearches: build.query<ISavedSearch[], {searchTerm?: string}>({
            query: (arg) => ({
                url: arg.searchTerm ? `search/savedSearch?nameFilter=${arg.searchTerm}` : 'search/savedSearch',
                method: 'GET',
            }),
            transformResponse: (result: ICollectionResponse<ISavedSearch>) => result.items
        }),
        getSavedSearchRefs: build.query<ISavedSearchRef[], void>({
            query: () => ({
                url: `search/savedSearch/refs`,
                method: 'GET',
            }),
            transformResponse: (result: ICollectionResponse<ISavedSearchRef>) => result.items
        }),
        getSavedSearchOnStorefrontCount: build.query<ICountResponse, {storefrontId: string}>({
            query: (arg) => ({
                url: `search/savedSearch/storefrontSavedSearchCount/${arg.storefrontId}`,
                method: 'GET',
            })
        }),
        getSavedSearch: build.query<ISavedSearch, {id: string}>({
            query: (arg) => ({
                url: `search/savedSearch/${arg.id}`,
                method: 'GET',
            }),
        }),
        getTopSavedSearches: build.query<ISavedSearch[], void>({
            query: () => ({
                url: 'search/savedSearch/top',
                method: 'GET',
            }),
            transformResponse: (result: ICollectionResponse<ISavedSearch>) => result.items
        }),
        saveSearch: build.mutation<IActionResponse, {savedSearch: ISavedSearch}>({
            query: (arg) => ({
                url: `search/savedSearch/save`,
                method: 'PUT',
                body: arg.savedSearch
            }),
            async onQueryStarted(arg, {dispatch, queryFulfilled}) {
                const result = await queryFulfilled;
                const id = result.data.id ?? arg.savedSearch.id;

                dispatch(
                    savedSearchApi.util.updateQueryData('getSavedSearches', {searchTerm: ''}, (draft) => {
                        const updatedSaved = cloneDeep(draft)
                        if(arg.savedSearch.id) {
                            const existingIndex = draft.findIndex(s => s.id === arg.savedSearch.id);

                            if (existingIndex > -1) {
                                updatedSaved[existingIndex] = arg.savedSearch
                            }
                        }
                        else{
                            const updatedNew = cloneDeep(arg.savedSearch)
                            updatedNew.id = id
                            updatedSaved.push(updatedNew)
                        }
                        Object.assign(draft, updatedSaved);
                    })
                );

                dispatch(
                    savedSearchApi.util.updateQueryData('getSavedSearch', {id: id}, (draft) => {
                        const updatedSaved = cloneDeep(arg.savedSearch)
                        updatedSaved.id = id
                        Object.assign(draft, updatedSaved);
                    })
                );

                dispatch(
                    savedSearchApi.util.updateQueryData('getSavedSearchRefs', undefined, (draft) => {
                        const lookupId = arg.savedSearch.id ?? id;
                        const existingIndex = draft.findIndex(s => s.id === lookupId);
                        if (existingIndex > -1) {
                            // Update existing ref, preserving fields not on ISavedSearch
                            draft[existingIndex] = {
                                ...draft[existingIndex],
                                id: id ?? arg.savedSearch.id,
                                name: arg.savedSearch.name,
                                shared: arg.savedSearch.shared,
                                image: arg.savedSearch.image,
                            };
                        } else {
                            // Add new ref
                            draft.push({
                                id: id,
                                name: arg.savedSearch.name,
                                shared: arg.savedSearch.shared,
                                image: arg.savedSearch.image,
                            });
                        }
                    })
                );
            }
        }),
        deleteSavedSearch: build.mutation<IActionResponse, string>({
            query: (arg) => ({
                url: `search/savedSearch/${arg}`,
                method: 'DELETE',
            }),
            async onQueryStarted(arg, {dispatch, queryFulfilled}) {
                const savedSearchesPatch = dispatch(
                    savedSearchApi.util.updateQueryData('getSavedSearches', {searchTerm: ''}, (draft) => {
                        const updatedSaved = cloneDeep(draft)
                        if(arg) {
                            const existingIndex = draft.findIndex(s => s.id === arg);
                            if (existingIndex > -1) {
                                updatedSaved.splice(existingIndex, 1)
                            }
                        }
                        Object.assign(draft, updatedSaved);
                    })
                )
                const topSavedSearchesPatch = dispatch(
                    savedSearchApi.util.updateQueryData('getTopSavedSearches', undefined, (draft) => {
                        const updatedSaved = cloneDeep(draft)
                        if(arg) {
                            const existingIndex = draft.findIndex(s => s.id === arg);
                            if (existingIndex > -1) {
                                updatedSaved.splice(existingIndex, 1)
                            }
                        }
                        Object.assign(draft, updatedSaved);
                    })
                )
                const getSavedSearchesPatch = dispatch(
                    savedSearchApi.util.updateQueryData('getSavedSearches', {}, (draft) => {
                        const updatedSaved = cloneDeep(draft)
                        if(arg) {
                            const existingIndex = draft.findIndex(s => s.id === arg);
                            if (existingIndex > -1) {
                                updatedSaved.splice(existingIndex, 1)
                            }
                        }
                        Object.assign(draft, updatedSaved);
                    })
                )
                const refsPatch = dispatch(
                    savedSearchApi.util.updateQueryData('getSavedSearchRefs', undefined, (draft) => {
                        const existingIndex = draft.findIndex(s => s.id === arg);
                        if (existingIndex > -1) {
                            draft.splice(existingIndex, 1);
                        }
                    })
                )

                try {
                    await queryFulfilled
                } catch {
                    savedSearchesPatch.undo();
                    topSavedSearchesPatch.undo();
                    getSavedSearchesPatch.undo();
                    refsPatch.undo();
                }
            }
        }),
        clearSearch: build.mutation<null, void>({
            queryFn: () => ({data: null}),
            invalidatesTags: [
                SEARCH_RESULTS_TYPE,
            ]
        }),
    })
})

export const {
    useLazyGetSavedSearchQuery,
    useGetSavedSearchRefsQuery,
    useLazyGetSavedSearchOnStorefrontCountQuery,
    useGetSavedSearchesQuery,
    useGetSavedSearchOnStorefrontCountQuery,
    useGetSavedSearchQuery,
    useGetTopSavedSearchesQuery,
    useSaveSearchMutation,
    useDeleteSavedSearchMutation,
    useClearSearchMutation,
} = savedSearchApi;

